import { useMemo } from "react";
import { Pressable, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  BAND_GLYPH,
  bandLabel,
  type IntensityGrid,
} from "@jackedlogs/shared";

import { resolveTheme } from "../theme";
import { useThemePreferenceContext } from "../ThemeContext";
import { ScaleLegend } from "../primitives/ScaleLegend";
import { makeGridStyles } from "./gridStyles";

// The day × week heat grid, lifted from the mobile Home program preview
// (v3.2.0) into the shared library so the composer's live preview and
// Home render the SAME glance. Presentational only: callers build the
// grid (buildIntensityGrid from a stored Program, or
// buildIntensityGridFromDraft from the editor draft) and own the
// selection state — Home pairs it with a read-only day detail, the
// composer with the actionable day panel.
// Every cell shows its value (Antenore 2026-06-11: the whole program's
// shape must read without tapping row by row); selection is a brand ring
// + surface tint on the row. Cells with prescribed work but no banded
// intensity (kind none/history/kg) render outlined with the set count
// ("18×") — a free day must never look like a rest day.
export function IntensityGridView({
  grid,
  selected,
  onSelect,
  dark,
  title,
}: {
  grid: IntensityGrid;
  selected: number | null;
  onSelect: (sessionIdx: number) => void;
  dark: boolean;
  // Section label above the grid; defaults to the Home preview's title.
  // Pass null to render none (the composer has its own section header).
  title?: string | null;
}) {
  const { t } = useTranslation();
  const family = useThemePreferenceContext()?.preference.family ?? "brand";
  const theme = resolveTheme(dark, family);
  const styles = useMemo(() => makeGridStyles(theme), [theme]);

  const heading =
    title === undefined
      ? t("mobile.preview.gridTitle", { defaultValue: "Intensity · day × week" })
      : title;

  return (
    <View>
      {heading ? <Text style={styles.sectionLabel}>{heading}</Text> : null}

      <View style={styles.gridHeaderRow}>
        <View style={styles.gridDayCol} />
        {Array.from({ length: grid.weeks }, (_, w) => (
          <Text key={w} style={styles.gridWeekHead}>{`W${w + 1}`}</Text>
        ))}
      </View>

      {grid.days.map((day) => {
        const isSel = day.sessionIdx === selected;
        return (
          <Pressable
            key={day.sessionIdx}
            accessibilityRole="button"
            accessibilityState={{ selected: isSel }}
            accessibilityLabel={`${day.name}${day.band ? ` · ${bandLabel(t, day.band)}` : ""}`}
            onPress={() => onSelect(day.sessionIdx)}
            style={({ pressed }) => [
              styles.gridRow,
              isSel && styles.gridRowSelected,
              pressed && styles.gridRowPressed,
            ]}
          >
            <Text style={[styles.gridDayNum, isSel && styles.gridDayNumSelected]}>
              {day.sessionIdx + 1}
            </Text>
            <View style={styles.gridCells}>
              {day.cells.map((cell, w) => {
                const free = !cell.band && cell.sets > 0;
                const c = theme.intensity[cell.band ?? "none"];
                return (
                  <View
                    key={w}
                    style={[
                      styles.gridCell,
                      { backgroundColor: c.fill },
                      free && styles.gridCellFree,
                    ]}
                  >
                    {/* Band glyph corner marker — the non-colour cue (W/M/H/X)
                        so the band never rides on fill colour alone. */}
                    {cell.band ? (
                      <Text style={[styles.gridCellBand, { color: c.on }]}>
                        {BAND_GLYPH[cell.band]}
                      </Text>
                    ) : null}
                    {cell.value ? (
                      <Text style={[styles.gridCellNum, { color: c.on }]}>{cell.value}</Text>
                    ) : free ? (
                      <Text style={[styles.gridCellNum, styles.gridCellFreeNum]}>
                        {`${cell.sets}×`}
                      </Text>
                    ) : null}
                  </View>
                );
              })}
            </View>
          </Pressable>
        );
      })}

      <ScaleLegend
        variant="bands"
        theme={theme}
        includeFree
        style={styles.legendRow}
      />
      <Text style={styles.gridHint}>
        {t("mobile.preview.gridHint", { defaultValue: "Tap a day to see its lifts." })}
      </Text>
    </View>
  );
}
